import React from 'react';
import { useTranslation } from 'react-i18next';
import { Hyperlink } from '../../../../components/atoms/Hyperlink';
import { Namespace } from '../../../../translation/namespace';
import { Breakpoints } from '../../../../types/Breakpoints';
import { TooltipPlacement } from '../../../../components/atoms/Tooltip';
import { useAppContext } from '../../../../contexts/AppContext/AppContext';
import { NavIcon } from './NavIcon';
import { FetchSubPanel } from '../../MainNavigationTypes';
import { PersonalNavItem } from '../../../../types/api/PersonalNavItem';
import { NextPanelButton } from '../SubPanels/NextPanelButton';

export type PersonalNavItemsProps = {
  personalNavItems: PersonalNavItem[];
  fetchSubPanel?: FetchSubPanel;
  panelLevel?: number;
  activeOpenButtonId?: string;
  setActiveOpenButtonId?: (activeOpenButtonId: string | undefined) => void;
};

/**
 * Renders a list of personal NavItems.
 */
export function MainNavigationPersonalNavItemList({
  personalNavItems,
  fetchSubPanel,
  panelLevel,
  activeOpenButtonId,
  setActiveOpenButtonId,
}: PersonalNavItemsProps) {
  const { t } = useTranslation(Namespace.MainNavigation);
  const { windowInnerWidth } = useAppContext();

  const getTitle = (personalNavItem: PersonalNavItem) => {
    let linkTitle = t(`${Namespace.phrases}:personalNode`);
    const additionalStats = [];

    if (personalNavItem.versionState === 'draft') {
      additionalStats.push(t(`${Namespace.glossary}:draft`));
    }

    if (personalNavItem.status && personalNavItem.status !== 'active') {
      additionalStats.push(t(`personalNavItem.${personalNavItem.status}`));
    }

    if (additionalStats.length > 0) {
      linkTitle += ` (${additionalStats.join(', ')})`;
    }

    return linkTitle;
  };

  /**
   *  no lists if there are no nav items at all
   */
  if (!personalNavItems || personalNavItems.length === 0) {
    return null;
  }

  return (
    <ul>
      {personalNavItems.map(personalNavItem => (
        <li className="main-navigation__item main-navigation__second-navitem" key={personalNavItem.link.url}>
          <div className="main-navigation__navitem-icon">
            <Hyperlink
              to={personalNavItem.link.url}
              className="main-navigation__link font-medium-15-19"
              title={getTitle(personalNavItem)}
              tooltipPlacement={
                windowInnerWidth > Breakpoints.s_smartphone_landscape ? TooltipPlacement.RIGHT : TooltipPlacement.BOTTOM
              }
            >
              {personalNavItem.status && <NavIcon status={personalNavItem.status} />}
              <span className="main-navigation__link-text">{personalNavItem.link.name}</span>
            </Hyperlink>
            {personalNavItem?.fetchUrl && fetchSubPanel && panelLevel && (
              <NextPanelButton
                panelName={personalNavItem.link.name}
                url={personalNavItem.link.url}
                fetchUrl={personalNavItem?.fetchUrl?.url}
                fetchSubPanel={fetchSubPanel}
                panelLevel={panelLevel}
                activeOpenButtonId={activeOpenButtonId}
                setActiveOpenButtonId={setActiveOpenButtonId}
              />
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
